import type {
  DashboardCockpitTodayAiRecentComparison,
  DashboardCockpitTodayAiSlice,
  DashboardCockpitTodayAiSummary,
} from '../types';

export type TodayAiComparisonKey =
  | 'attemptedCalls'
  | 'answeredCalls'
  | 'convertedCalls'
  | 'answerRate'
  | 'conversionRate'
  | 'aiConsumedMinutes';

export type TodayAiComparisonTrend = 'above' | 'below' | 'steady';

export interface TodayAiComparisonRow {
  key: TodayAiComparisonKey;
  currentValue: number;
  averageValue: number;
  deltaValue: number;
  deltaPercent: number | null;
  trend: TodayAiComparisonTrend;
  isRate: boolean;
  isMinutes: boolean;
}

export interface TodayAiEfficiencyMetrics {
  aiConsumedSeconds: number;
  aiSecondsPerAnswer: number | null;
  aiSecondsPerConversion: number | null;
  answersPer100AiMinutes: number | null;
  conversionsPer100AiMinutes: number | null;
  comparisons: TodayAiEfficiencyComparisonRow[];
}

export type TodayAiEfficiencyComparisonKey =
  | 'aiConsumedSeconds'
  | 'aiSecondsPerAnswer'
  | 'aiSecondsPerConversion'
  | 'answersPer100AiMinutes'
  | 'conversionsPer100AiMinutes';

export type TodayAiEfficiencyInterpretation = 'neutral' | 'lowerIsBetter' | 'higherIsBetter';

export type TodayAiEfficiencyTrend = 'above' | 'below' | 'improved' | 'degraded' | 'steady';

export interface TodayAiEfficiencyComparisonRow {
  key: TodayAiEfficiencyComparisonKey;
  currentValue: number;
  averageValue: number;
  deltaPercent: number;
  trend: TodayAiEfficiencyTrend;
  interpretation: TodayAiEfficiencyInterpretation;
}

export interface TodayAiCostReturnMetrics {
  totalEstimatedCostUsd: number;
  estimatedCostPerAnswer: number | null;
  estimatedCostPerConversion: number | null;
}

type TodayAiComparableScope = DashboardCockpitTodayAiSummary & {
  recentComparison: DashboardCockpitTodayAiRecentComparison | null;
};

const COUNT_STEADY_THRESHOLD_PERCENT = 10;
const RATE_STEADY_THRESHOLD_POINTS = 2;
const EFFICIENCY_STEADY_THRESHOLD_PERCENT = 10;

/**
 * Round derived dashboard ratios before rendering or asserting them.
 */
function roundMetric(value: number, decimals = 2) {
  const multiplier = 10 ** decimals;
  return Math.round(value * multiplier) / multiplier;
}

function resolveTrend(deltaValue: number, options: { isRate: boolean }) {
  const { isRate } = options;
  const threshold = isRate ? RATE_STEADY_THRESHOLD_POINTS : COUNT_STEADY_THRESHOLD_PERCENT;
  if (Math.abs(deltaValue) < threshold) {
    return 'steady';
  }
  return deltaValue > 0 ? 'above' : 'below';
}

function resolveEfficiencyTrend(
  deltaPercent: number,
  interpretation: TodayAiEfficiencyInterpretation,
): TodayAiEfficiencyTrend {
  if (Math.abs(deltaPercent) < EFFICIENCY_STEADY_THRESHOLD_PERCENT) {
    return 'steady';
  }
  if (interpretation === 'neutral') {
    return deltaPercent > 0 ? 'above' : 'below';
  }
  if (interpretation === 'lowerIsBetter') {
    return deltaPercent < 0 ? 'improved' : 'degraded';
  }
  return deltaPercent > 0 ? 'improved' : 'degraded';
}

function buildEfficiencyComparisonRow(
  key: TodayAiEfficiencyComparisonKey,
  currentValue: number | null,
  averageValue: number | null | undefined,
  interpretation: TodayAiEfficiencyInterpretation,
): TodayAiEfficiencyComparisonRow | null {
  if (currentValue == null || averageValue == null || averageValue <= 0) {
    return null;
  }
  const deltaPercent = ((currentValue - averageValue) / averageValue) * 100;
  return {
    key,
    currentValue: roundMetric(currentValue),
    averageValue: roundMetric(averageValue),
    deltaPercent: roundMetric(deltaPercent),
    trend: resolveEfficiencyTrend(deltaPercent, interpretation),
    interpretation,
  };
}

function buildCountComparisonRow(
  key: Exclude<TodayAiComparisonKey, 'answerRate' | 'conversionRate'>,
  currentValue: number,
  averageValue: number,
): TodayAiComparisonRow | null {
  if (averageValue <= 0) {
    return null;
  }
  const deltaPercent = ((currentValue - averageValue) / averageValue) * 100;
  return {
    key,
    currentValue,
    averageValue: roundMetric(averageValue),
    deltaValue: roundMetric(deltaPercent),
    deltaPercent: roundMetric(deltaPercent),
    trend: resolveTrend(deltaPercent, { isRate: false }),
    isRate: false,
    isMinutes: key === 'aiConsumedMinutes',
  };
}

function buildRateComparisonRow(
  key: Extract<TodayAiComparisonKey, 'answerRate' | 'conversionRate'>,
  currentValue: number,
  averageValue: number,
): TodayAiComparisonRow {
  const deltaPoints = currentValue - averageValue;
  return {
    key,
    currentValue,
    averageValue: roundMetric(averageValue),
    deltaValue: roundMetric(deltaPoints),
    deltaPercent: null,
    trend: resolveTrend(deltaPoints, { isRate: true }),
    isRate: true,
    isMinutes: false,
  };
}

/**
 * Build comparable rows from the backend's same-elapsed-day baseline.
 *
 * The baseline already matches today's local-day elapsed window, so the
 * frontend only calculates presentation deltas and trend labels.
 */
export function buildTodayAiRecentComparison(
  todayAi: TodayAiComparableScope | DashboardCockpitTodayAiSlice | null,
  aiConsumedSeconds: number | null | undefined,
): TodayAiComparisonRow[] {
  const comparison = todayAi?.recentComparison;
  if (!todayAi || !comparison?.hasSufficientSample) {
    return [];
  }

  const rows: TodayAiComparisonRow[] = [
    buildCountComparisonRow(
      'attemptedCalls',
      todayAi.attemptedCalls,
      comparison.attemptedCallsAverage,
    ),
    buildCountComparisonRow(
      'answeredCalls',
      todayAi.answeredCalls,
      comparison.answeredCallsAverage,
    ),
    buildCountComparisonRow(
      'convertedCalls',
      todayAi.convertedCalls,
      comparison.convertedCallsAverage,
    ),
    buildRateComparisonRow(
      'answerRate',
      todayAi.answerRate,
      comparison.answerRate,
    ),
    buildRateComparisonRow(
      'conversionRate',
      todayAi.conversionRate,
      comparison.conversionRate,
    ),
  ].filter((row): row is TodayAiComparisonRow => Boolean(row));

  const consumedSeconds = Number(aiConsumedSeconds || 0);
  const consumedSecondsAverage = Number(comparison.aiConsumedSecondsAverage || 0);
  if (consumedSeconds > 0 && consumedSecondsAverage > 0) {
    const aiConsumedMinutesRow = buildCountComparisonRow(
      'aiConsumedMinutes',
      consumedSeconds / 60,
      consumedSecondsAverage / 60,
    );
    if (aiConsumedMinutesRow) {
      rows.push(aiConsumedMinutesRow);
    }
  }

  return rows;
}

/**
 * Derive same-day AI productivity metrics from the snapshot payload.
 *
 * Returns null when there is no AI consumption today because every metric in
 * this section depends on consumed time as the denominator or headline value.
 */
export function buildTodayAiEfficiency(
  aiConsumedSeconds: number | null | undefined,
  answeredCalls: number,
  convertedCalls: number,
  comparison?: DashboardCockpitTodayAiRecentComparison | null,
): TodayAiEfficiencyMetrics | null {
  const safeConsumedSeconds = Number(aiConsumedSeconds || 0);
  if (safeConsumedSeconds <= 0) {
    return null;
  }
  const aiConsumedMinutes = safeConsumedSeconds / 60;
  const metrics: TodayAiEfficiencyMetrics = {
    aiConsumedSeconds: safeConsumedSeconds,
    aiSecondsPerAnswer: answeredCalls > 0 ? safeConsumedSeconds / answeredCalls : null,
    aiSecondsPerConversion: convertedCalls > 0 ? safeConsumedSeconds / convertedCalls : null,
    answersPer100AiMinutes: aiConsumedMinutes > 0
      ? roundMetric((answeredCalls / aiConsumedMinutes) * 100)
      : null,
    conversionsPer100AiMinutes: aiConsumedMinutes > 0
      ? roundMetric((convertedCalls / aiConsumedMinutes) * 100)
      : null,
    comparisons: [],
  };

  if (!comparison?.hasSufficientSample) {
    return metrics;
  }

  metrics.comparisons = [
    buildEfficiencyComparisonRow(
      'aiConsumedSeconds',
      metrics.aiConsumedSeconds,
      comparison.aiConsumedSecondsAverage,
      'neutral',
    ),
    buildEfficiencyComparisonRow(
      'aiSecondsPerAnswer',
      metrics.aiSecondsPerAnswer,
      comparison.aiSecondsPerAnswer,
      'lowerIsBetter',
    ),
    buildEfficiencyComparisonRow(
      'aiSecondsPerConversion',
      metrics.aiSecondsPerConversion,
      comparison.aiSecondsPerConversion,
      'lowerIsBetter',
    ),
    buildEfficiencyComparisonRow(
      'answersPer100AiMinutes',
      metrics.answersPer100AiMinutes,
      comparison.answersPer100AiMinutes,
      'higherIsBetter',
    ),
    buildEfficiencyComparisonRow(
      'conversionsPer100AiMinutes',
      metrics.conversionsPer100AiMinutes,
      comparison.conversionsPer100AiMinutes,
      'higherIsBetter',
    ),
  ].filter((row): row is TodayAiEfficiencyComparisonRow => Boolean(row));

  return metrics;
}

/**
 * Derive cost-per-result metrics while preserving cost permission semantics.
 *
 * A null cost means the backend intentionally hid the cost surface from the
 * current user, so the frontend must omit the whole cost section.
 */
export function buildTodayAiCostReturn(
  totalEstimatedCostUsd: number | null | undefined,
  answeredCalls: number,
  convertedCalls: number,
): TodayAiCostReturnMetrics | null {
  if (totalEstimatedCostUsd == null) {
    return null;
  }
  const safeCost = Number(totalEstimatedCostUsd || 0);
  return {
    totalEstimatedCostUsd: safeCost,
    estimatedCostPerAnswer: answeredCalls > 0 ? safeCost / answeredCalls : null,
    estimatedCostPerConversion: convertedCalls > 0 ? safeCost / convertedCalls : null,
  };
}
